import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { CloseIcon } from '../common/CloseIcon';
import { Trans, useTranslation } from 'react-i18next';
import ExpanderArrow from '../BuildingModal/ExpanderArrow';
import { CSSTransition } from 'react-transition-group';
import OffMarketSellIcon from '../FeedEvents/OffMarketSellIcon';
import OffMarketBuyIcon from '../FeedEvents/OffMarketBuyIcon';
import { getCookie } from '../utils/utils';
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';
import LocationIcon from '../FeedEvents/LocationIcon';
import mapboxgl from 'mapbox-gl';
import { fetchPOIsForBuilding } from '../AppContent/LandingPage/LandingPage.helpers';
import { saveEmptyBuildingPage } from '../BuildingModal/Report.fetchExistingReport';
import BuildingSelectionMap from '../BuildingModal/BuildingSelectionMap';
import { OffMarketToastMessage } from '../utils/types';
import SharePromptModal from '../common/SharePromptModal';
import { shareOffMarketWillingness, formatAddressString } from '../../utils/shareUtils';
import TagManager from 'react-gtm-module';
import { ImageLightbox } from '../ImageLightbox';
import PropertyImageUpload from '../common/PropertyImageUpload';
import { useLandingPageOffMarketForm } from '../../hooks/useLandingPageOffMarketForm';
import BuyingTemplateIndicator from '../BuildingModal/BuyingTemplateIndicator';

import './LandingPageCouldBuyOrCouldSellModal.scss';
import '../MessagesModal/MessagesModal.scss';
import '../BuildingModal/BuyingTemplateIndicator.scss';
import PriceEstimationBanner from '../BuildingModal/PriceEstimationBanner';
import { usePriceEstimation } from '../../hooks/usePriceEstimation';
import PreEstimationModal from '../BuildingModal/PreEstimationModal';
import { PriceEstimationReport } from '../PriceEstimation/PriceEstimationReport';
import { TFunction } from 'i18next';

/**
 * Maps a submit-failure error code from POST /off-market-willingness to a
 * user-facing alert; unknown codes fall back to the generic submit error.
 */
const showSubmitError = (t: TFunction<string>, errorCode?: string) => {
  switch (errorCode) {
    case 'LIMIT_REACHED':
      alert(t('building.offMarket.modal.sellListingsLimitReached'));
      break;
    case 'COULD_BUY_LIMIT_REACHED':
      alert(t('building.offMarket.toast.couldBuyLimitReached'));
      break;
    case 'ALREADY_SELLING_IN_BUILDING':
      alert(t('building.offMarket.modal.alreadySellingInBuilding'));
      break;
    default:
      alert(t('landingPage.couldBuyOrCouldSellModal.submitError'));
      break;
  }
};

type OffMarketAction = 'buy' | 'sell';

export interface PropertyDetails {
  description?: string;
  size?: number;
  rooms?: string;
  floor?: number;
  hasElevator?: boolean;
  hasBalcony?: boolean;
  renovationStatus?: string;
  buildingType?: string;
  images?: any[];
  address?: string;
  location?: {
    lat: number;
    lng: number;
  };
  buildingId?: string;
  buildingGeometry?: {
    type: string;
    coordinates: number[][][];
  };
}

type Props = {
  countryOfFeedContent: string;
  isOpen: boolean;
  onClose: () => void;
  actionType: OffMarketAction;
  isLoading: boolean;
  isLoggedIn?: boolean;
  userEmail?: string | null;
  setLoginModalVisible: (visible: boolean) => void;
  setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal: (message: OffMarketToastMessage | null) => void;
  onSelectBuilding?: (buildingId: string) => void;
  fetchConversations: () => void;
};


/**
 * Route-driven could-sell / could-buy form (`/could-sell`, `/could-buy`):
 * address and building pick, home details, then the interest submit and its
 * share prompt.
 */
const LandingPageCouldBuyOrCouldSellModal: React.FC<Props> = ({
  countryOfFeedContent,
  isOpen,
  onClose,
  actionType,
  isLoading,
  isLoggedIn,
  userEmail,
  setLoginModalVisible,
  setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal,
  onSelectBuilding,
  fetchConversations,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  // Use the custom hook for form data and enhancement management
  const {
    formData,
    setFormData,
    enhancementStatus,
    setEnhancementStatus,
    isPurchasingCredits,
    handleBuyCredits,
    saveAllFormData,
    clearAllFormData,
    extendedData,
    setExtendedData,
    // Template-related
    hasTemplate,
    isLoadingTemplate,
    isUsingTemplate,
    shouldSaveTemplate,
    setShouldSaveTemplate,
    isTemplateAnimating,
    saveBuyingTemplate,
    deleteBuyingTemplate,
    // Image helpers
    toggleImageVersion,
    regenerateImage,
    isRegenerating
  } = useLandingPageOffMarketForm({
    actionType,
    isLoggedIn: isLoggedIn || false,
    isOpen
  });

  // Local state for UI-specific things
  const [isQueryingBuilding, setIsQueryingBuilding] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAddressIncomplete, setIsAddressIncomplete] = useState<boolean>(false);
  const [showBuildingSelectionMap, setShowBuildingSelectionMap] = useState<boolean>(false);
  const [addressMismatchError, setAddressMismatchError] = useState<boolean>(false);
  const [showSharePrompt, setShowSharePrompt] = useState<boolean>(false);
  const [createdWillingnessData, setCreatedWillingnessData] = useState<{ eventId: string; buildingId: string; address: any; isMatch: boolean; conversationId: string | null; } | null>(null);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [showBuildingValidationMessage, setShowBuildingValidationMessage] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isPriceReportOpen, setIsPriceReportOpen] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);
  const [addressObject, setAddressObject] = useState<any>(null);
  // Lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isPreEstimationModalOpen, setIsPreEstimationModalOpen] = useState<boolean>(false);
  const [forceShowPreFields, setForceShowPreFields] = useState<boolean>(false);
  const [sizeError, setSizeError] = useState<string>('');
  const [showAddressValidation, setShowAddressValidation] = useState<boolean>(false);

  const requestDelete = () => {
    setShowDeleteConfirm(true);
  };

  // Auto-hide address validation message after 5 seconds
  useEffect(() => {
    if (showAddressValidation) {
      const timer = setTimeout(() => {
        setShowAddressValidation(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showAddressValidation]);

  async function getAddressComponents(freshAddressComponents?: any, freshLocation?: { lat: number; lng: number }) {
    // Use the freshly provided components if available (to avoid React state timing issues)
    if (freshAddressComponents) {
      return freshAddressComponents;
    }

    // Otherwise, prefer the reverse-geocoded components from the clicked building
    let addressComponents = extendedData.selectedAddressComponents ||
      (extendedData.addressResults.length > 0 ? extractAddressComponents(extendedData.addressResults) : null);

    // Use fresh location if provided, otherwise fall back to state
    const location = freshLocation || extendedData.location;
    if (!addressComponents && location) {
      const res = await reverseGeocodePreferred(location.lat, location.lng);
      if (res) {
        addressComponents = res.components;
        setExtendedData(prev => ({ ...prev, address: res.formatted }));
      }
    }
    return addressComponents;
  }

  // Price estimation integration
  const {
    estimation,
    status: estimationStatus,
    error: estimationError,
    estimationId,
    isPolling,
    startEstimation // Function to manually start estimation
  } = usePriceEstimation(
    {
      address: addressObject,
      description: formData.description,
      size: formData.size,
      rooms: formData.rooms,
      floor: formData.floor,
      hasElevator: formData.hasElevator,
      hasBalcony: formData.hasBalcony,
      renovationStatus: formData.renovationStatus,
      buildingType: formData.buildingType,
      building_id: extendedData.buildingId,
    },
    extendedData.isDetailsExpanded,
    isLoggedIn || false,
    addressObject
  );

  // Add askingPrice to formData
  const [askingPrice, setAskingPrice] = useState<string>('');

  // Restore location state when building is selected
  useEffect(() => {
    if (extendedData.location && extendedData.buildingIsSelected) {
      setShowBuildingSelectionMap(true);
    }
  }, [extendedData.location, extendedData.buildingIsSelected]);

  // Restore scroll position after payment return
  useEffect(() => {
    if (isOpen && actionType === 'sell') {
      const params = new URLSearchParams(window.location.search);
      const paymentStatus = params.get('payment');
      const sessionId = params.get('session_id');

      // Check for payment return
      if (paymentStatus === 'success' || paymentStatus === 'cancelled' || sessionId) {
        console.log('Landing page modal: Checking for saved scroll position after payment');
        const savedScrollPosition = localStorage.getItem(`landing_page_scroll_position_${actionType}`);
        console.log('Saved scroll position:', savedScrollPosition);

        if (savedScrollPosition && modalRef.current) {
          // If details are expanded (which they should be for sell flow), wait for animation
          // Note: For sell flow, details must be expanded to access image upload
          const waitTime = extendedData.isDetailsExpanded ? 400 : 100; // 300ms for CSSTransition + 100ms buffer
          console.log('Waiting', waitTime, 'ms before restoring scroll (details expanded:', extendedData.isDetailsExpanded, ')');

          setTimeout(() => {
            if (modalRef.current) {
              const scrollPos = parseInt(savedScrollPosition, 10);
              console.log('Attempting to restore scroll position to:', scrollPos);
              modalRef.current.scrollTop = scrollPos;
              // Verify the scroll was actually applied
              setTimeout(() => {
                if (modalRef.current) {
                  const actualScrollPos = modalRef.current.scrollTop;
                  console.log('Actual scroll position after restore:', actualScrollPos, 'Target was:', scrollPos);
                }
              }, 50);
              localStorage.removeItem(`landing_page_scroll_position_${actionType}`);
            }
          }, waitTime);
        }
      }
    }
  }, [isOpen, actionType, extendedData.isDetailsExpanded]);

  // Function to get lat/lng from Mapbox instead of Google
  const getLatLngFromMapbox = async (address: string): Promise<{ lat: number; lng: number }> => {
    const encodedAddress = encodeURIComponent(address);
    const mapboxAccessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxAccessToken}&limit=1`
    );

    if (!response.ok) {
      throw new Error('Failed to geocode address with Mapbox');
    }

    const data = await response.json();

    if (!data.features || data.features.length === 0) {
      throw new Error('No results found for the address');
    }

    const [lng, lat] = data.features[0].center;
    return { lat, lng };
  };

  // Mapbox reverse geocoding that returns structured components too
  const reverseGeocodeMapboxStructured = async (lat: number, lng: number): Promise<{
    formatted: string;
    components: {
      houseNumber: string; street: string; city: string; region: string; country: string; countryCode?: string; postcode: string;
    }
  } | null> => {
    const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const resp = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&types=address&limit=1`
    );
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!data.features || data.features.length === 0) return null;
    const feature = data.features[0];
    const ctx = (type: string) => feature.context?.find((c: any) => c.id.startsWith(type))?.text || '';
    const components = {
      houseNumber: feature.address || '',
      street: feature.text || '',
      city: ctx('place') || ctx('locality') || '',
      region: ctx('region') || ctx('district') || '',
      country: ctx('country') || '',
      countryCode: feature.context?.find((c: any) => c.id.startsWith('country'))?.short_code?.toUpperCase(),
      postcode: ctx('postcode') || '',
    };
    return { formatted: feature.place_name, components };
  };

  // Preferred reverse geocoding: Google first, fallback to Mapbox
  const reverseGeocodePreferred = async (lat: number, lng: number) => {
    const m = await reverseGeocodeMapboxStructured(lat, lng);
    return m;
  };

  // Map country names to ISO country codes for Google Places API
  const countryToISOCode: { [key: string]: string } = {
    'Finland': 'fi',
    'finland': 'fi',
    'Sweden': 'se',
    'sweden': 'se',
    'Norway': 'no',
    'norway': 'no',
    'Denmark': 'dk',
    'denmark': 'dk',
    'Estonia': 'ee',
    'estonia': 'ee',
    'United States': 'us',
    'united states': 'us',
    'USA': 'us',
    'usa': 'us',
    'UK': 'gb',
    'uk': 'gb',
    'United Kingdom': 'gb',
    'united kingdom': 'gb',
    'Germany': 'de',
    'germany': 'de',
    'France': 'fr',
    'france': 'fr',
    'Spain': 'es',
    'spain': 'es',
    'Italy': 'it',
    'italy': 'it'
    // Add more country mappings as needed
  };

  // Get the ISO country code from countryOfFeedContent
  const getCountryCode = (country: string): string | undefined => {
    const normalized = country.toLowerCase().trim();
    return countryToISOCode[country] || countryToISOCode[normalized];
  };

  // Function to validate if address has all required components using structured data
  const validateAddressFromResults = (results: any[]): boolean => {
    if (!results || results.length === 0) return false;

    const result = results[0];

    // Method 1: Check if Google considers this a complete street address
    if (result.types && result.types.includes('street_address')) {
      return true;
    }

    // Method 2: Check for essential components
    if (!result.address_components) return false;

    const components = result.address_components;
    const componentTypes = new Set<string>();

    // Collect all component types
    components.forEach((component: any) => {
      if (component.types && Array.isArray(component.types)) {
        component.types.forEach((type: string) => componentTypes.add(type));
      }
    });

    // Check for required components (relaxed): street and city/locality are enough
    const hasRoute = componentTypes.has('route');
    const hasLocality = componentTypes.has('locality') ||
      componentTypes.has('postal_town') ||
      componentTypes.has('administrative_area_level_3') ||
      componentTypes.has('sublocality') ||
      componentTypes.has('sublocality_level_1');

    // A valid address should have at least street and city/locality.
    return hasRoute && hasLocality;
  };

  // Function to extract address components from Google Places results
  const extractAddressComponents = (results: any[]) => {
    if (!results || results.length === 0) {
      return null;
    }

    const result = results[0];
    const components = result.address_components || [];

    // Helper function to find component by type
    const findComponent = (types: string[]) => {
      for (const type of types) {
        const component = components.find((c: any) => c.types?.includes(type));
        if (component) return component;
      }
      return null;
    };

    // Extract components
    const houseNumberComponent = findComponent(['house_number']);
    const routeComponent = findComponent(['route']);
    const cityComponent = findComponent(['locality', 'postal_town', 'administrative_area_level_3', 'sublocality', 'sublocality_level_1']);
    const regionComponent = findComponent(['administrative_area_level_1', 'administrative_area_level_2']);
    const countryComponent = findComponent(['country']);
    const postalCodeComponent = findComponent(['postal_code']);

    return {
      houseNumber: houseNumberComponent?.long_name || '',
      street: routeComponent?.long_name || '',
      city: cityComponent?.long_name || '',
      region: regionComponent?.long_name || '',
      country: countryComponent?.long_name || '',
      countryCode: countryComponent?.short_name || '',
      postcode: postalCodeComponent?.long_name || '',
      // Full formatted address as backup
      formattedAddress: result.formatted_address || ''
    };
  };

  /**
   * Submits the interest to POST /off-market-willingness (or opens login when
   * logged out), then shows the match toast and share prompt; alerts on a
   * failed submit.
   */
  const onConfirm = async (propertyDetails?: PropertyDetails) => {
    if (!isLoggedIn) {
      TagManager.dataLayer({
        dataLayer: {
          event: `${actionType === 'sell' ? 'submit_sell_landing_page_not_logged_in' : 'submit_buy_landing_page_not_logged_in'}`,
          category: 'User Interaction',
          action: 'Click',
          label: `${actionType === 'sell' ? 'Sell Submit Landing Page Not Logged In' : 'Buy Submit Landing Page Not Logged In'}`,
        },
      });
      // JAA-226: defer marketing consent for the rest of this browser
      // session so the user can return to this flow and confirm after
      // signup/login without the marketing modal interrupting.
      sessionStorage.setItem('marketing_consent_deferred', '1');
      setLoginModalVisible(true);
      return;
    }
    if (!extendedData.address || !extendedData.location) {
      TagManager.dataLayer({
        dataLayer: {
          event: `${actionType === 'sell' ? 'submit_sell_landing_page_address_not_selected' : 'submit_buy_landing_page_address_not_selected'}`,
          category: 'User Interaction',
          action: 'Click',
          label: `${actionType === 'sell' ? 'Sell Submit Landing Page Address Not Selected' : 'Buy Submit Landing Page Address Not Selected'}`,
        },
      });
      alert(t('building.offMarket.modal.pleaseSelectAddress'));
      return;
    }
    TagManager.dataLayer({
      dataLayer: {
        event: `${actionType === 'sell' ? 'sell_submit_landing_page' : 'buy_submit_landing_page'}`,
        category: 'User Interaction',
        action: 'Click',
        label: `${actionType === 'sell' ? 'Sell Submit Landing Page' : 'Buy Submit Landing Page'}`,
      },
    });

    setIsSubmitting(true);

    try {
      // Construct the request body
      const requestBody: any = {
        // Set could_sell or could_buy based on actionType
        [`could_${actionType}`]: true,
      };

      // Always include selected_building with address data
      if (extendedData.buildingId && extendedData.buildingGeometry) {
        // If we found a building, include all the building data
        requestBody.selected_building = {
          id: extendedData.buildingId,
          centroid: extendedData.location,
          geometry: extendedData.buildingGeometry,
          addresses: [addressObject], // Backend expects addresses array at root level
          properties: {
            name: extendedData.address,
            addresses: [addressObject], // Also include in properties for consistency
            type: "apartments"
          }
        };
      } else {
        // If no building found, still send basic structure with address
        requestBody.selected_building = {
          id: `temp_${Date.now()}`, // Temporary ID for buildings not in database
          centroid: extendedData.location,
          geometry: { type: 'Polygon', coordinates: [] }, // Empty geometry
          addresses: [addressObject],
          properties: {
            name: extendedData.address,
            addresses: [addressObject],
            type: "apartments"
          }
        };
      }

      // Add property details
      if (propertyDetails?.description) {
        requestBody.property_description = propertyDetails.description;
      }
      if (propertyDetails?.size) {
        requestBody.property_size = propertyDetails.size;
      }
      if (propertyDetails?.rooms) {
        requestBody.property_rooms = propertyDetails.rooms;
      }
      if (propertyDetails?.floor !== undefined) {
        requestBody.property_floor = propertyDetails.floor;
      }
      if (propertyDetails?.hasElevator !== undefined) {
        requestBody.property_has_elevator = propertyDetails.hasElevator;
      }
      if (propertyDetails?.hasBalcony !== undefined) {
        requestBody.property_has_balcony = propertyDetails.hasBalcony;
      }
      if (propertyDetails?.renovationStatus) {
        requestBody.property_renovation_status = propertyDetails.renovationStatus;
      }
      if (propertyDetails?.buildingType) {
        requestBody.property_building_type = propertyDetails.buildingType;
      }

      // Add asking price if provided (sell flow only)
      if (actionType === 'sell' && askingPrice.trim() !== '') {
        requestBody.property_asking_price = Number(askingPrice);
      }

      // Add price estimation ID if available
      if (estimationId) {
        requestBody.price_estimation_id = estimationId;
        requestBody.price_estimation_status = estimationStatus;
      }

      // Add images if present
      if (propertyDetails?.images && propertyDetails.images.length > 0) {
        requestBody.property_images = propertyDetails.images.map(img => ({
          id: img.id,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl,
          originalUrl: img.originalUrl,
          originalThumbnailUrl: img.originalThumbnailUrl,
          enhancedUrl: img.enhancedUrl,
          enhancedThumbnailUrl: img.enhancedThumbnailUrl,
          isDisplayOptimized: img.isDisplayOptimized,
        }));
      }

      // Add location data even if no building found
      requestBody.property_address = extendedData.address;
      requestBody.property_location = extendedData.location;

      // Add tracking data
      requestBody.user_agent = navigator.userAgent;
      requestBody.source = 'landing_page';

      // Send the request
      const response = await fetch(`${process.env.REACT_APP_API_URL}/off-market-willingness`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw errorData || new Error(`Request failed with status ${response.status}`);
      }

      const buildingWithPOIs = await fetchPOIsForBuilding(
        {
          id: extendedData.buildingId,
          centroid: extendedData.location,
          geometry: extendedData.buildingGeometry,
          addresses: [addressObject], // Backend expects addresses array at root level
          properties: {
            name: extendedData.address,
            addresses: [addressObject], // Also include in properties for consistency
          },
          pois: []
        }
        , extendedData.location);

      await saveEmptyBuildingPage({
        selectedBuilding: buildingWithPOIs
      });

      const responseData = await response.json();

      let isMatch = false;
      if (responseData.is_new_could_sell_and_buyers || responseData.is_new_could_buy_and_sellers) {
        isMatch = true;
      }

      // Store the created willingness data for sharing
      if (responseData.willingness_id) {
        setCreatedWillingnessData({
          eventId: responseData.willingness_id,
          buildingId: extendedData.buildingId || responseData.building_id || '',
          address: addressObject,
          isMatch: isMatch,
          // Exactly one buy-match conversation → land the buyer straight in it (JAA-275).
          conversationId:
            responseData.is_new_could_buy_and_sellers &&
            responseData.conversation_ids?.length === 1
              ? responseData.conversation_ids[0]
              : null,
        });
      }

      // Set the state for toast messages
      if (responseData.is_new_could_sell_and_buyers) {
        setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal('COULD_SELL_AND_BUYERS');
      } else if (responseData.is_new_could_buy_and_sellers) {
        setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal('COULD_BUY_AND_SELLERS');
      } else {
        setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal(actionType === 'sell' ? 'COULD_SELL_NO_BUYERS' : 'COULD_BUY_NO_SELLERS');
      }

      // Show share prompt (keep modal open)
      setShowSharePrompt(true);

      // Navigation will happen after share prompt is dismissed

      if (responseData.is_new_could_sell_and_buyers || responseData.is_new_could_buy_and_sellers) {
        fetchConversations();
      }

    } catch (error) {
      console.error('Error submitting off-market willingness:', error);
      const errorCode = (
        typeof error === 'object'
        && error !== null
        && 'error_code' in error
      )
        ? (error as { error_code?: string }).error_code
        : undefined;

      showSubmitError(t, errorCode);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Frontend validation: size cannot be 0 or negative if provided
    if (formData.size.trim() !== '' && Number(formData.size) <= 0) {
      setSizeError(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
      return;
    }
    setSizeError('');
    // Check if building is selected first
    if (!extendedData.buildingId || !extendedData.buildingIsSelected) {
      setShowBuildingValidationMessage(true);
      // Scroll to the map section to make it visible
      if (modalRef.current) {
        const mapElement = modalRef.current.querySelector('.could-buy-or-could-sell-modal__autocomplete');
        if (mapElement) {
          mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    const propertyDetails: PropertyDetails = {};

    if (formData.description) propertyDetails.description = formData.description;
    if (formData.size) propertyDetails.size = Number(formData.size) <= 0 ? undefined : Number(formData.size);
    if (formData.rooms) propertyDetails.rooms = formData.rooms;
    if (formData.floor) propertyDetails.floor = Number(formData.floor);
    propertyDetails.hasElevator = formData.hasElevator;
    propertyDetails.hasBalcony = formData.hasBalcony;
    if (formData.renovationStatus) propertyDetails.renovationStatus = formData.renovationStatus;
    if (formData.buildingType) propertyDetails.buildingType = formData.buildingType;
    if (extendedData.address) propertyDetails.address = extendedData.address;
    if (extendedData.location) propertyDetails.location = extendedData.location;
    if (extendedData.buildingId) propertyDetails.buildingId = extendedData.buildingId;
    if (extendedData.buildingGeometry) propertyDetails.buildingGeometry = extendedData.buildingGeometry;

    // Only include successfully uploaded images (those that have an id and no error)
    const validImages = formData.images.filter(img => img.id && !img.error && !img.isUploading);
    if (validImages.length > 0) {
      propertyDetails.images = validImages;
    }

    // Save template if requested (buy action only)
    if (shouldSaveTemplate && actionType === 'buy' && isLoggedIn) {
      saveBuyingTemplate();
    }

    // Call onConfirm and wait for it to complete
    await onConfirm(propertyDetails);

    // Reset form only after successful submission
    if (isLoggedIn) {
      clearAllFormData();
      // Clean up any saved scroll position
      localStorage.removeItem(`landing_page_scroll_position_${actionType}`);
    }
  };

  const resetForm = () => {
    clearAllFormData();
    setIsAddressIncomplete(false);
    setShowBuildingSelectionMap(false);
    setAddressMismatchError(false);
    setShowSharePrompt(false);
    setCreatedWillingnessData(null);
    setIsSharing(false);
    setShowBuildingValidationMessage(false);
    // Clean up any saved scroll position
    localStorage.removeItem(`landing_page_scroll_position_${actionType}`);
    onClose();
  };

  const handleShareWillingness = async () => {
    if (!createdWillingnessData) return;

    setIsSharing(true);
    await shareOffMarketWillingness({
      buildingId: createdWillingnessData.buildingId,
      eventId: createdWillingnessData.eventId,
      isSellInterest: actionType === 'sell',
      address: createdWillingnessData.address,
      t: t,
      isMatch: createdWillingnessData.isMatch
    });

    setIsSharing(false);

    // Always close after sharing attempt (whether successful or not)
    handleCloseSharePrompt();
  };

  const handleCloseSharePrompt = () => {
    // Capture match flag and building id before clearing state — we need both
    // to decide where to navigate next.
    const wasMatch = createdWillingnessData?.isMatch ?? false;
    const buildingIdToOpen = createdWillingnessData?.buildingId;
    const matchedConversationId = createdWillingnessData?.conversationId ?? null;

    setShowSharePrompt(false);

    // Non-match: navigate to the building page (existing behavior).
    if (!wasMatch && buildingIdToOpen && onSelectBuilding) {
      onSelectBuilding(buildingIdToOpen);
    }

    // Clear data and close the main modal
    setCreatedWillingnessData(null);
    onClose();

    // Match: push /direct-messages AFTER onClose() so we win the race against
    // the parent's onClose pushing history.push('/') (see LandingPage.tsx
    // where this modal is mounted on /could-buy or /could-sell URLs).
    // Straight into the conversation when the buy-match produced exactly one (JAA-275).
    if (wasMatch) {
      history.push(
        matchedConversationId
          ? `/direct-messages?conversation-id=${matchedConversationId}`
          : '/direct-messages',
      );
    }
  };

  const toggleDetailsExpansion = () => {
    setExtendedData(prev => ({ ...prev, isDetailsExpanded: !prev.isDetailsExpanded }));
  };

  const handleBuildingSelect = async (buildingId: string | null, buildingGeometry: any | null, userClickedCoordinates: { lat: number; lng: number } | null) => {
    setExtendedData(prev => ({
      ...prev,
      buildingId,
      buildingGeometry,
      buildingIsSelected: true
    }));

    if (buildingId && buildingGeometry && userClickedCoordinates) {
      const res = await reverseGeocodePreferred(userClickedCoordinates.lat, userClickedCoordinates.lng);
      if (res) {
        setExtendedData(prev => ({
          ...prev,
          buildingAddress: res.formatted,
          address: res.formatted,
          selectedAddressComponents: res.components,
          addressResults: [],
          location: { lat: userClickedCoordinates.lat, lng: userClickedCoordinates.lng }
        }));
        setIsAddressIncomplete(false);
      }
      // Pass the fresh address components directly to avoid React state timing issues
      const addressComponents = await getAddressComponents(res?.components, { lat: userClickedCoordinates.lat, lng: userClickedCoordinates.lng });
      if (!addressComponents) {
        alert(t('building.offMarket.modal.invalidAddress'));
        return;
      }
      const addressObject = {
        country: addressComponents.country,
        region: addressComponents.region,
        city: addressComponents.city,
        postcode: addressComponents.postcode,
        street: addressComponents.street,
        houseNumber: addressComponents.houseNumber,
        coordinates: {
          lat: userClickedCoordinates.lat,
          lng: userClickedCoordinates.lng
        }
      };
      setAddressObject(addressObject);

      setAddressMismatchError(false);
      // Clear the validation message when a building is successfully selected
      setShowBuildingValidationMessage(false);
      // Also clear the address validation message for price estimation
      setShowAddressValidation(false);
    } else if (!buildingId) {
      // Building was deselected
      setAddressMismatchError(false);
    }
  };

  const handleAddressChange = (address: string) => {
    setExtendedData(prev => ({
      ...prev,
      address,
      location: null,
      buildingId: null,
      buildingGeometry: null,
      addressResults: [],
      selectedAddressComponents: null,
      buildingIsSelected: false,
      buildingAddress: ''
    }));
    // Reset states when user types
    setIsAddressIncomplete(false);
    setShowBuildingSelectionMap(false);
    setAddressMismatchError(false);
    setShowBuildingValidationMessage(false);
  };

  const queryBuildingAtLocation = async (lat: number, lng: number, userAddress: string): Promise<{ id: string | null; geometry: any | null }> => {
    return new Promise((resolve) => {
      // Set Mapbox access token
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

      // Create a temporary hidden map container
      const tempContainer = document.createElement('div');
      tempContainer.style.width = '100px';
      tempContainer.style.height = '100px';
      tempContainer.style.visibility = 'hidden';
      tempContainer.style.position = 'absolute';
      document.body.appendChild(tempContainer);

      // Create a temporary map instance
      const tempMap = new mapboxgl.Map({
        container: tempContainer,
        style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
        center: [lng, lat],
        zoom: 17, // High zoom level to ensure building features are loaded
        interactive: false
      });

      let isCleanedUp = false;
      const cleanup = () => {
        if (isCleanedUp) return;
        isCleanedUp = true;
        try {
          tempMap.remove();
        } catch (err) {
          // Ignore errors during map removal
        }
        try {
          if (document.body.contains(tempContainer)) {
            document.body.removeChild(tempContainer);
          }
        } catch (err) {
          // Ignore errors during container removal
        }
      };

      tempMap.on('load', async () => {
        // Query for building features at the location
        const point = tempMap.project([lng, lat]);
        const features = tempMap.queryRenderedFeatures(point, {
          layers: ['building']
        });

        let buildingId: string | null = null;
        let buildingGeometry: any | null = null;

        if (features.length > 0) {
          const buildingFeature = features[0];

          // Get building ID
          buildingId = buildingFeature.id?.toString() || null;

          // Get building geometry if it's a polygon
          if (buildingFeature.geometry?.type === 'Polygon') {
            buildingGeometry = {
              type: buildingFeature.geometry.type,
              coordinates: buildingFeature.geometry.coordinates
            };

            // Do not verify mismatch here. User will confirm the clicked building.
          }
        }

        // Clean up
        cleanup();

        resolve({ id: buildingId, geometry: buildingGeometry });
      });

      tempMap.on('error', (error) => {
        console.error('Error loading temporary map:', error);
        // Clean up on error
        cleanup();
        resolve({ id: null, geometry: null });
      });
    });
  };

  // Update form field handlers
  const updateFormField = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressSelect = async (address: string) => {
    try {
      const results = await geocodeByAddress(address);
      if (results.length === 0) return;

      // Update extended data with address and clear building selection
      setExtendedData(prev => ({
        ...prev,
        address,
        addressResults: results,
        buildingAddress: '',
        buildingIsSelected: false,
        selectedAddressComponents: null
      }));

      // Validate address completeness using structured results data
      const isAddressValid = validateAddressFromResults(results);
      setIsAddressIncomplete(!isAddressValid);

      if (!isAddressValid) {
        // Don't query for building if address is incomplete
        setExtendedData(prev => ({
          ...prev,
          location: null,
          buildingId: null,
          buildingGeometry: null
        }));
        setShowBuildingSelectionMap(false);
        return;
      }

      // Use Mapbox API to get lat/lng instead of Google's getLatLng
      const latLng = await getLatLngFromMapbox(address);

      setExtendedData(prev => ({
        ...prev,
        location: latLng
      }));

      // Show the building selection map for user to click on the correct building
      setShowBuildingSelectionMap(true);

      // Auto-query for building at this location as a fallback
      setIsQueryingBuilding(true);

      // Query Mapbox for building features at this location
      const { id, geometry } = await queryBuildingAtLocation(latLng.lat, latLng.lng, address);

      if (id) {
        setExtendedData(prev => ({
          ...prev,
          buildingId: id,
          buildingGeometry: geometry
        }));
        console.log('Auto-detected building ID:', id);
      } else {
        setExtendedData(prev => ({
          ...prev,
          buildingId: null,
          buildingGeometry: null
        }));
        console.log('No building auto-detected, user should click on map');
      }

      setIsQueryingBuilding(false);

    } catch (error) {
      console.error('Error selecting address:', error);
      setIsQueryingBuilding(false);
    }
  };

  // Removed old handleImageUpload - using PropertyImageUpload component now

  const handleBuyCreditsWithLoginCheck = () => {
    console.log('handleBuyCreditsWithLoginCheck called, isLoggedIn:', isLoggedIn);
    if (!isLoggedIn) {
      // Save form data before closing modal to show login
      console.log('User not logged in, saving form data and showing login modal');
      saveAllFormData();
      // Save scroll position
      if (modalRef.current) {
        const scrollPosition = modalRef.current.scrollTop;
        localStorage.setItem(`landing_page_scroll_position_${actionType}`, scrollPosition.toString());
        console.log('Saving scroll position before login:', scrollPosition);
      }
      setLoginModalVisible(true);
      return;
    }
    console.log('User logged in, calling handleBuyCredits');
    // Save scroll position before redirecting to Stripe
    if (modalRef.current) {
      const scrollPosition = modalRef.current.scrollTop;
      localStorage.setItem(`landing_page_scroll_position_${actionType}`, scrollPosition.toString());
      console.log('Saving scroll position before Stripe redirect:', scrollPosition);
    }
    handleBuyCredits();
  };

  // Lightbox handlers
  const openLightbox = (index: number) => {
    setLightboxInitialIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteBuyingTemplate?.();
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle scrolling to bottom when images are selected
  const handleImagesSelected = () => {
    // Ensure details section is expanded to show images
    if (!extendedData.isDetailsExpanded) {
      setExtendedData(prev => ({ ...prev, isDetailsExpanded: true }));
    }

    // Small delay to ensure DOM updates and images start loading
    setTimeout(() => {
      if (modalRef.current) {
        // Smooth scroll the modal itself to the bottom (it's the scrollable container)
        modalRef.current.scrollTo({
          top: modalRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 300); // Slightly longer delay to account for expansion animation
  };

  if (!isOpen) return null;

  // Use createPortal to render outside the parent component hierarchy
  return (
    <>
      {ReactDOM.createPortal(
        <div className="could-buy-or-could-sell-modal-overlay" style={{ display: showSharePrompt ? 'none' : 'flex' }}>
          <div className="could-buy-or-could-sell-modal" ref={modalRef}>
            <div className="could-buy-or-could-sell-modal__close-button-wrapper">
              <button
                className="could-buy-or-could-sell-modal__close-button"
                onClick={resetForm}
                disabled={isLoading}
              >
                <CloseIcon size={20} />
              </button>
            </div>
            <div className="could-buy-or-could-sell-modal__header">
              <h2 className="could-buy-or-could-sell-modal__title">
                {actionType === 'sell' ? (
                  <OffMarketSellIcon
                    width="26px"
                    height="26px"
                    className="could-buy-or-could-sell-modal__sell-icon"
                  />
                ) : (
                  <OffMarketBuyIcon
                    width="26px"
                    height="26px"
                    className="could-buy-or-could-sell-modal__buy-icon"
                  />
                )}
                <Trans
                  i18nKey={`landingPage.couldBuyOrCouldSellModal.${actionType}.title`}
                />
              </h2>
            </div>

            <div className="could-buy-or-could-sell-modal__content">
              <p className="could-buy-or-could-sell-modal__description">
                <Trans
                  i18nKey={`landingPage.couldBuyOrCouldSellModal.${actionType}.description`}
                />
              </p>

              <div className="could-buy-or-could-sell-modal__form-field">
                <label htmlFor="property-address">
                  <Trans i18nKey="building.offMarket.modal.address" />
                  <span className="could-buy-or-could-sell-modal__location-icon">
                    <LocationIcon
                      isCouldBuyOrCouldSellModal
                    />
                  </span>
                </label>
                <PlacesAutocomplete
                  value={extendedData.address}
                  onChange={handleAddressChange}
                  onSelect={handleAddressSelect}
                  searchOptions={{
                    componentRestrictions: {
                      country: getCountryCode(countryOfFeedContent) || undefined
                    }
                  }}
                >
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div className="could-buy-or-could-sell-modal__autocomplete">
                      <input
                        {...getInputProps({
                          placeholder: t('building.offMarket.modal.addressPlaceholder'),
                          className: 'could-buy-or-could-sell-modal__input',
                          id: 'property-address',
                          disabled: isQueryingBuilding
                        })}
                      />
                      {suggestions.length > 0 && (
                        <div className="could-buy-or-could-sell-modal__suggestions">
                          {loading && (
                            <div className="could-buy-or-could-sell-modal__suggestion could-buy-or-could-sell-modal__suggestion--loading">
                              {t('building.offMarket.modal.loading')}
                            </div>
                          )}
                          {suggestions.map((suggestion) => (
                            <div
                              {...getSuggestionItemProps(suggestion, {
                                className: `could-buy-or-could-sell-modal__suggestion ${suggestion.active ? 'could-buy-or-could-sell-modal__suggestion--active' : ''
                                  }`
                              })}
                              key={suggestion.placeId}
                            >
                              <span>{suggestion.description}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </PlacesAutocomplete>
              </div>

              {/* Building Selection Map */}
              {extendedData.location && (
                <>
                  <BuildingSelectionMap
                    location={extendedData.location}
                    onBuildingSelect={handleBuildingSelect}
                    isVisible={showBuildingSelectionMap}
                    isQueryingBuilding={isQueryingBuilding}
                    address={extendedData.address}
                    buildingId={extendedData.buildingId}
                    buildingGeometry={extendedData.buildingGeometry}
                    showBuildingSelectionMap={showBuildingSelectionMap}
                    isAddressIncomplete={isAddressIncomplete}
                    addressMismatchError={addressMismatchError}
                    actionType={actionType}
                    buildingAddress={extendedData.buildingAddress}
                  />
                  {showBuildingValidationMessage && (
                    <div className="could-buy-or-could-sell-modal__validation-message">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="could-buy-or-could-sell-modal__validation-icon"
                      >
                        <path
                          d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                          stroke="#FF5A5F"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 6V10"
                          stroke="#FF5A5F"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="10" cy="14" r="0.5" fill="#FF5A5F" />
                      </svg>
                      <span>{t('building.offMarket.modal.selectBuildingOnMap')}</span>
                    </div>
                  )}
                </>
              )}

              {actionType === 'buy' && isUsingTemplate && (
                <div className="template-chip">
                  <span className="template-chip__text">
                    {t('building.offMarket.template.usingTemplate')}
                  </span>
                  <button
                    className="template-chip__clear"
                    onClick={requestDelete}
                    disabled={isDeleting}
                    aria-label={t('building.offMarket.template.clear')}
                  >
                    {isDeleting ? (
                      <div className="template-chip__clear-spinner" />
                    ) : (
                      <svg id="fi_3096673"
                        enableBackground="new 0 0 512 512"
                        height="13" viewBox="0 0 512 512"
                        width="13"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="#fff"
                      >
                        <g><path
                          d="m424 64h-88v-16c0-26.467-21.533-48-48-48h-64c-26.467 0-48 21.533-48 48v16h-88c-22.056 0-40 17.944-40 40v56c0 8.836 7.164 16 16 16h8.744l13.823 290.283c1.221 25.636 22.281 45.717 47.945 45.717h242.976c25.665 0 46.725-20.081 47.945-45.717l13.823-290.283h8.744c8.836 0 16-7.164 16-16v-56c0-22.056-17.944-40-40-40zm-216-16c0-8.822 7.178-16 16-16h64c8.822 0 16 7.178 16 16v16h-96zm-128 56c0-4.411 3.589-8 8-8h336c4.411 0 8 3.589 8 8v40c-4.931 0-331.567 0-352 0zm313.469 360.761c-.407 8.545-7.427 15.239-15.981 15.239h-242.976c-8.555 0-15.575-6.694-15.981-15.239l-13.751-288.761h302.44z"></path><path d="m256 448c8.836 0 16-7.164 16-16v-208c0-8.836-7.164-16-16-16s-16 7.164-16 16v208c0 8.836 7.163 16 16 16z"></path><path d="m336 448c8.836 0 16-7.164 16-16v-208c0-8.836-7.164-16-16-16s-16 7.164-16 16v208c0 8.836 7.163 16 16 16z"></path><path d="m176 448c8.836 0 16-7.164 16-16v-208c0-8.836-7.164-16-16-16s-16 7.164-16 16v208c0 8.836 7.163 16 16 16z">
                          </path></g></svg>
                    )}
                  </button>
                </div>
              )}

              <div className={`could-buy-or-could-sell-modal__form ${isUsingTemplate ? 'could-buy-or-could-sell-modal__form--is-using-template' : ''}`}>
                <div className={`could-buy-or-could-sell-modal__expander ${actionType === 'buy' && extendedData.isDetailsExpanded ? 'could-buy-or-could-sell-modal__expander--buy' : ''}`}>
                  <button
                    onClick={toggleDetailsExpansion}
                    className="could-buy-or-could-sell-modal__expander-button"
                  >
                    <Trans i18nKey="building.offMarket.modal.additionalDetails" />
                    <ExpanderArrow isExpanded={extendedData.isDetailsExpanded} />
                  </button>
                </div>

                <CSSTransition
                  in={extendedData.isDetailsExpanded}
                  timeout={300}
                  classNames="details-expand"
                  unmountOnExit
                  onEntered={() => {
                    // If we have a saved scroll position, attempt to restore it as soon as the expand animation finishes
                    const savedScrollPosition = localStorage.getItem(`landing_page_scroll_position_${actionType}`);
                    if (savedScrollPosition && modalRef.current) {
                      const target = parseInt(savedScrollPosition, 10);
                      let attempts = 0;
                      const maxAttempts = 10; // ~1s with 100ms intervals
                      const tryRestore = () => {
                        if (!modalRef.current) return;
                        modalRef.current.scrollTop = target;
                        // Verify and retry if not yet applied (due to ongoing layout)
                        if (Math.abs(modalRef.current.scrollTop - target) > 2 && attempts < maxAttempts) {
                          attempts += 1;
                          setTimeout(tryRestore, 100);
                        } else {
                          localStorage.removeItem(`landing_page_scroll_position_${actionType}`);
                        }
                      };
                      // Small delay to ensure final layout after onEntered
                      setTimeout(tryRestore, 0);
                    }
                  }}
                >
                  <div className={`could-buy-or-could-sell-modal__additional-details
                    ${isTemplateAnimating && actionType === 'buy' ? 'could-buy-or-could-sell-modal__additional-details--animating'
                      : ''}`}>
                    {/* Template indicator for buy action */}
                    {actionType === 'buy' && isLoggedIn && (
                      <BuyingTemplateIndicator
                        hasTemplate={hasTemplate}
                        shouldSaveTemplate={shouldSaveTemplate}
                        onToggleSaveTemplate={setShouldSaveTemplate}
                        isLoadingTemplate={isLoadingTemplate}
                        isTemplateAnimating={isTemplateAnimating}
                      />
                    )}

                    {/* Price field and estimation (sell flow only) */}
                    {actionType === 'sell' && (
                      <>
                        <PriceEstimationBanner
                          estimation={estimation}
                          estimationStatus={estimationStatus}
                          isPolling={isPolling}
                          estimationError={estimationError}
                          formData={formData}
                          askingPrice={askingPrice}
                          isLoggedIn={isLoggedIn}
                          setAskingPrice={setAskingPrice}
                          setForceShowPreFields={setForceShowPreFields}
                          setIsPreEstimationModalOpen={setIsPreEstimationModalOpen}
                          setIsPriceReportOpen={setIsPriceReportOpen}
                          addressObject={addressObject}
                          onMissingAddress={() => setShowAddressValidation(true)}
                          showAddressValidation={showAddressValidation}
                          setLoginModalVisible={setLoginModalVisible}
                        />
                      </>
                    )}
                    <div className="could-buy-or-could-sell-modal__form-field">
                      <label htmlFor="property-description">
                        <Trans i18nKey="building.offMarket.modal.description" />
                      </label>
                      <textarea
                        id="property-description"
                        className="could-buy-or-could-sell-modal__textarea"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder={actionType === 'buy' ? t('building.offMarket.modal.buyingInterestDescriptionPlaceholder') : t('building.offMarket.modal.sellingInterestDescriptionPlaceholder')}
                        rows={3}
                        maxLength={1100}
                      />
                      <span className="could-buy-or-could-sell-modal__textarea-counter">{formData.description.length} / 1100</span>
                    </div>
                    <div className="could-buy-or-could-sell-modal__form-row">
                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                        <label htmlFor="property-size">
                          {actionType === 'buy' ? <Trans i18nKey="building.offMarket.modal.sizeClass" /> : <Trans i18nKey="building.offMarket.modal.size" />}
                        </label>
                        <input
                          id="property-size"
                          type="number"
                          className="could-buy-or-could-sell-modal__input"
                          value={formData.size}
                          onChange={(e) => {
                            const next = e.target.value;
                            setFormData(prev => ({ ...prev, size: next }));
                            if (next.trim() === '') {
                              setSizeError('');
                            } else if (Number(next) <= 0) {
                              setSizeError(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
                            } else {
                              setSizeError('');
                            }
                          }}
                          placeholder={t('building.offMarket.modal.sizePlaceholder')}
                          min={1}
                        />
                        {sizeError && (
                          <div className="could-buy-or-could-sell-modal__input-error" role="alert">
                            {sizeError}
                          </div>
                        )}
                      </div>

                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                        <label htmlFor="property-rooms">
                          <Trans i18nKey="building.offMarket.modal.roomsLabel" />
                        </label>
                        <select
                          id="property-rooms"
                          className="could-buy-or-could-sell-modal__select"
                          value={formData.rooms}
                          onChange={(e) => setFormData(prev => ({ ...prev, rooms: e.target.value }))}
                        >
                          <option value="">
                            {t('building.offMarket.modal.selectRooms')}
                          </option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5+">5+</option>
                        </select>
                      </div>
                    </div>
                    <div className="could-buy-or-could-sell-modal__form-row">
                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                        <label htmlFor="property-floor">
                          <Trans i18nKey="building.offMarket.modal.floor" />
                        </label>
                        <input
                          id="property-floor"
                          type="number"
                          className="could-buy-or-could-sell-modal__input"
                          value={formData.floor}
                          onChange={(e) => setFormData(prev => ({ ...prev, floor: e.target.value }))}
                          placeholder={t('building.offMarket.modal.floorPlaceholder')}
                        />
                      </div>

                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--half">
                        <label htmlFor="property-renovation">
                          <Trans i18nKey="building.offMarket.modal.renovation" />
                        </label>
                        <select
                          id="property-renovation"
                          className="could-buy-or-could-sell-modal__select"
                          value={formData.renovationStatus}
                          onChange={(e) => setFormData(prev => ({ ...prev, renovationStatus: e.target.value }))}
                        >
                          <option value="">
                            {t('building.offMarket.modal.selectRenovation')}
                          </option>
                          <option value="recently_renovated">
                            {t('building.offMarket.modal.recentlyRenovated')}
                          </option>
                          <option value="good_condition">
                            {t('building.offMarket.modal.goodCondition')}
                          </option>
                          <option value="needs_renovation">
                            {t('building.offMarket.modal.needsRenovation')}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div className="could-buy-or-could-sell-modal__form-row could-buy-or-could-sell-modal__form-row--checkboxes">
                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--checkbox">
                        <input
                          id="property-elevator"
                          type="checkbox"
                          checked={formData.hasElevator}
                          onChange={(e) => setFormData(prev => ({ ...prev, hasElevator: e.target.checked }))}
                        />
                        <label htmlFor="property-elevator">
                          <Trans i18nKey="building.offMarket.modal.hasElevator" />
                        </label>
                      </div>

                      <div className="could-buy-or-could-sell-modal__form-field could-buy-or-could-sell-modal__form-field--checkbox">
                        <input
                          id="property-balcony"
                          type="checkbox"
                          checked={formData.hasBalcony}
                          onChange={(e) => setFormData(prev => ({ ...prev, hasBalcony: e.target.checked }))}
                        />
                        <label htmlFor="property-balcony">
                          <Trans i18nKey="building.offMarket.modal.hasBalcony" />
                        </label>
                      </div>
                    </div>

                    {/* Image Upload Section */}
                    {actionType === 'sell' && (
                      <PropertyImageUpload
                        images={formData.images}
                        setImages={(newImages) => {
                          if (typeof newImages === 'function') {
                            setFormData(prev => ({ ...prev, images: newImages(prev.images) }));
                          } else {
                            setFormData(prev => ({ ...prev, images: newImages }));
                          }
                        }}
                        maxImages={30}
                        enhancementStatus={enhancementStatus}
                        onEnhancementStatusUpdate={setEnhancementStatus}
                        onOpenLightbox={openLightbox}
                        onBuyCredits={handleBuyCreditsWithLoginCheck}
                        isPurchasingCredits={isPurchasingCredits}
                        isLoggedIn={isLoggedIn || false}
                        setLoginModalVisible={setLoginModalVisible}
                        onImagesSelected={handleImagesSelected}
                        onUploadingChange={setIsLoadingImages}
                      />
                    )}
                  </div>
                </CSSTransition>
              </div>
            </div>

            <div className="could-buy-or-could-sell-modal__footer">
              <button
                className="could-buy-or-could-sell-modal__button could-buy-or-could-sell-modal__button--cancel"
                onClick={resetForm}
                disabled={isLoading || isSubmitting}
              >
                <Trans i18nKey="building.offMarket.modal.cancel" />
              </button>
              <button
                className="could-buy-or-could-sell-modal__button could-buy-or-could-sell-modal__button--confirm"
                onClick={handleSubmit}
                disabled={isLoading || isSubmitting || formData.images.some(img => img.isUploading) || !extendedData.address || isLoadingImages}
              >
                {isSubmitting ? (
                  <div className="could-buy-or-could-sell-modal__button-spinner"></div>
                ) : (
                  <Trans i18nKey="building.offMarket.modal.confirm" />
                )}
              </button>
            </div>
            {isPriceReportOpen && estimation && (
              <PriceEstimationReport
                isOpen={isPriceReportOpen}
                onClose={() => setIsPriceReportOpen(false)}
                estimation={estimation}
              />
            )}
            {isPreEstimationModalOpen && (
              <PreEstimationModal
                setIsPreEstimationModalOpen={setIsPreEstimationModalOpen}
                updateFormField={updateFormField}
                startEstimation={startEstimation}
                formData={formData}
                setForceShowPreFields={setForceShowPreFields}
                setSizeError={setSizeError}
                sizeError={sizeError}
                isLoggedIn={isLoggedIn}
                setLoginModalVisible={setLoginModalVisible}
              />
            )}
            {showDeleteConfirm && (
              <div className="end-chat-confirmation-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
                <div className="end-chat-confirmation" onClick={(e) => e.stopPropagation()}>
                  <div className="end-chat-confirmation__header">
                    <h3>
                      <Trans i18nKey="building.offMarket.template.deleteConfirmTitle">Remove template</Trans>
                    </h3>
                    <button
                      className="end-chat-confirmation__close"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                    >
                      <CloseIcon color="#202125" size={16} />
                    </button>
                  </div>
                  <div className="end-chat-confirmation__content">
                    <p>
                      <Trans i18nKey="building.offMarket.template.deleteConfirmText">Are you sure you want to remove this template?</Trans>
                    </p>
                  </div>
                  <div className="end-chat-confirmation__actions">
                    <button
                      className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                    >
                      <Trans i18nKey="building.offMarket.template.cancel">Cancel</Trans>
                    </button>
                    <button
                      className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                      onClick={confirmDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? t('building.offMarket.template.loading', 'Loading...') : <Trans i18nKey="building.offMarket.template.delete">Remove</Trans>}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body // Render directly to the body element
      )}
      <ImageLightbox
        isOpen={isLightboxOpen}
        images={formData.images}
        initialIndex={lightboxInitialIndex}
        onClose={closeLightbox}
        onToggleVersion={toggleImageVersion}
        onRegenerate={regenerateImage}
        isRegenerating={isRegenerating}
        enhancementCredits={enhancementStatus?.credits ?? null}
      />
      <SharePromptModal
        isOpen={showSharePrompt}
        onClose={handleCloseSharePrompt}
        onShare={handleShareWillingness}
        actionType={actionType}
        buildingAddress={createdWillingnessData ? formatAddressString(createdWillingnessData.address) : undefined}
        isSharing={isSharing}
        isMatch={createdWillingnessData ? createdWillingnessData.isMatch : false}
        eventId={createdWillingnessData?.eventId}
        buildingId={createdWillingnessData?.buildingId}
        userEmail={userEmail || undefined}
      />
    </>
  );
};

export default LandingPageCouldBuyOrCouldSellModal;